/** Storage 오브젝트 교체 시 캐시 무효화하려면 이 상수만 올리면 됩니다. */
const CACHE_KEY = 'timer_1min_aac_v1';
const DB_NAME = 'ClassPlannerTimerSound';
const DB_VERSION = 1;
const STORE = 'blobs';

const BUCKET = 'app-public-assets';
const OBJECT_PATH = 'timer/TIMER_1MIN.m4a';

let memoryBlobUrl: string | null = null;
let inflightResolve: Promise<string | null> | null = null;

function getSupabaseTimerPublicUrl(): string | null {
  const base = (process.env.REACT_APP_SUPABASE_URL || '').trim().replace(/\/$/, '');
  if (!base) return null;
  return `${base}/storage/v1/object/public/${BUCKET}/${OBJECT_PATH}`;
}

/** 우선순위: 전용 URL → 버셀 동일 출처 → Supabase 폴백. IDB 키는 URL과 무관해 재사용된다. */
export function getTimerSoundCandidateUrls(): string[] {
  const list: string[] = [];
  const override = (process.env.REACT_APP_TIMER_1MIN_PUBLIC_URL || '').trim();
  if (override) list.push(override);
  if (typeof window !== 'undefined' && window.location?.origin) {
    list.push(`${window.location.origin}/sounds/TIMER_1MIN.m4a`);
  } else {
    list.push('/sounds/TIMER_1MIN.m4a');
  }
  const supa = getSupabaseTimerPublicUrl();
  if (supa) list.push(supa);
  return Array.from(new Set(list.filter(Boolean)));
}

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGet(key: string): Promise<ArrayBuffer | undefined> {
  const db = await idbOpen();
  try {
    return await new Promise<ArrayBuffer | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).get(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve(r.result as ArrayBuffer | undefined);
    });
  } finally {
    db.close();
  }
}

async function idbPut(key: string, body: ArrayBuffer): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r = tx.objectStore(STORE).put(body, key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

async function fetchTimerSoundArrayBufferFromNetwork(): Promise<ArrayBuffer | null> {
  for (const url of getTimerSoundCandidateUrls()) {
    try {
      const res = await fetch(url, {
        method: 'GET',
        cache: 'force-cache',
        credentials: 'omit',
        mode: 'cors',
      });
      if (!res.ok) continue;
      const buf = await res.arrayBuffer();
      if (buf.byteLength > 0) return buf;
    } catch {
      continue;
    }
  }
  return null;
}

/**
 * 공개 URL에서 1회 fetch 후 IndexedDB에 저장합니다.
 * 이후 탭·세션에서 네트워크 없이 IDB → Blob URL으로 재생합니다.
 */
export async function getCachedTimerOneMinBlobUrl(): Promise<string | null> {
  if (memoryBlobUrl) return memoryBlobUrl;
  if (inflightResolve) return inflightResolve;

  inflightResolve = (async () => {
    try {
      const cached = await idbGet(CACHE_KEY);
      if (cached && cached.byteLength > 0) {
        memoryBlobUrl = URL.createObjectURL(new Blob([cached], { type: 'audio/mp4' }));
        return memoryBlobUrl;
      }
    } catch {
      /* IDB 미지원·거부 시 네트워크만 사용 */
    }

    const buf = await fetchTimerSoundArrayBufferFromNetwork();
    if (!buf) return null;
    try {
      await idbPut(CACHE_KEY, buf);
    } catch {
      /* 저장 실패해도 Blob URL 재생은 가능 */
    }
    memoryBlobUrl = URL.createObjectURL(new Blob([buf], { type: 'audio/mp4' }));
    return memoryBlobUrl;
  })();

  try {
    return await inflightResolve;
  } finally {
    inflightResolve = null;
  }
}

/** 슬라이드 탭 진입 시 백그라운드로 프리로드(첫 타이머 1분 진입 시 지연 감소). */
export function primeTimerOneMinSoundCache(): void {
  void getCachedTimerOneMinBlobUrl();
}

let chimeAudio: HTMLAudioElement | null = null;
let chimePausedForSlideTimerSync = false;

function ensureChimeAudio(): HTMLAudioElement {
  if (!chimeAudio) {
    chimeAudio = new Audio();
    chimeAudio.setAttribute('playsInline', 'true');
    chimeAudio.preload = 'auto';
  }
  return chimeAudio;
}

/**
 * 타이머「시작」버튼 등 사용자 제스처 안에서 호출하세요.
 * 브라우저가 나중에 `play()`를 막지 않도록 짧게 재생·일시정지로 오디오를 잠금 해제합니다.
 */
export async function unlockSlideTimerOneMinuteChimeAudio(): Promise<void> {
  const blobUrl = await getCachedTimerOneMinBlobUrl();
  if (!blobUrl) return;
  const a = ensureChimeAudio();
  a.src = blobUrl;
  a.load();
  try {
    a.muted = true;
    a.volume = 0;
    await a.play();
    a.pause();
    a.currentTime = 0;
  } catch {
    /* 일부 환경에서 무음 재생도 거부될 수 있음 */
  } finally {
    a.muted = false;
    a.volume = 1;
    chimePausedForSlideTimerSync = false;
  }
}

export async function playTimerOneMinuteChime(): Promise<void> {
  const blobUrl = await getCachedTimerOneMinBlobUrl();
  if (!blobUrl) return;
  chimePausedForSlideTimerSync = false;
  const a = ensureChimeAudio();
  a.src = blobUrl;
  a.load();
  a.muted = false;
  a.volume = 1;
  a.currentTime = 0;
  await a.play().catch(() => {
    /* 자동재생 정책 등 */
  });
}

export function pauseSlideTimerChimeForTimerPause(): void {
  const a = chimeAudio;
  if (!a) return;
  const t = a.currentTime;
  /** 재생 중(0초 직후·버퍼링 포함) 또는 이미 진행된 위치면 재개 시 이어서 재생 */
  chimePausedForSlideTimerSync = !a.ended && (!a.paused || t > 0.02);
  a.pause();
}

export function resumeSlideTimerChimeAfterTimerPause(): void {
  if (!chimeAudio) return;
  const a = chimeAudio;
  if (a.ended) {
    chimePausedForSlideTimerSync = false;
    return;
  }
  const shouldResume =
    chimePausedForSlideTimerSync || (a.paused && a.currentTime > 0.02 && !a.ended);
  if (!shouldResume) return;
  chimePausedForSlideTimerSync = false;
  a.muted = false;
  if (a.volume === 0) a.volume = 1;
  void a.play().catch(() => {});
}

/** 사용자가 타이머를 중지하거나 새 타이머를 시작할 때만 호출. 카운트다운이 0이 된 자연 종료에서는 호출하지 말 것(1분 알림 끝까지 재생). */
export function stopSlideTimerChimeAudioHard(): void {
  chimePausedForSlideTimerSync = false;
  if (!chimeAudio) return;
  chimeAudio.pause();
  chimeAudio.currentTime = 0;
}
